import { MapPin, Radio } from 'lucide-react';
import type { PublisherConfig, SelfServiceFormData } from '../../types/selfService';
import { displayedName } from '../../data/publisherConfigs';
import { DMA_REGIONS } from '../../data/dmaList';
import { stateNameForCode } from './shared/feasibilityCopy';

export interface TargetingRow {
  icon: typeof MapPin;
  label: string;
  value: string;
}

interface TargetingRowInput {
  targetMarkets?: string[];
  targetingMode?: 'default' | 'custom';
  geoMode?: 'states' | 'dmas' | 'radius';
  selectedStates?: string[];
  selectedDMAs?: string[];
  geoTargetCenter?: { lat: number; lng: number; label: string } | null;
  geoTargetRadiusMiles?: number | null;
  geoTargetZips?: string[];
}

const summarize = (label: string, names: string[]): string => {
  if (names.length === 0) return label;
  if (names.length <= 3) return `${names.length} ${label} (${names.join(', ')})`;
  return `${names.length} ${label} (${names.slice(0, 3).join(', ')} +${names.length - 3} more)`;
};

export const dmaNameForCode = (code: string): string =>
  DMA_REGIONS.find((d) => d.code === code)?.name ?? code;

// CTV-674: buyers can now target DMAs beyond the publisher's own coverage
// markets (anything within 50 miles), so display code→name resolution must
// fall back to the canonical Nielsen list instead of showing a raw code.
// Publisher-configured names still win for their own markets.
export const resolveDmaName = (
  config: Pick<PublisherConfig, 'markets'> | null | undefined,
  code: string,
): string =>
  config?.markets.find((m) => m.dmaCode === code)?.dmaName ?? dmaNameForCode(code);

// CTV-558: single source of truth for the "Targeting / Channel / Markets"
// row rendered in the wizard sidebar (Step 2) and on the Review step.
// Switches by single-vs-multi market and (in custom mode) by geo sub-mode
// so the row updates the moment the buyer changes states / DMAs / radius.
export const getTargetingRow = (
  input: TargetingRowInput,
  config: Pick<PublisherConfig, 'markets' | 'displayName' | 'name' | 'slug'>,
): TargetingRow => {
  const isSingleMarket = config.markets.length === 1;
  const targetingMode = input.targetingMode ?? 'default';

  if (!isSingleMarket) {
    const names = (input.targetMarkets ?? [])
      .map((code) => resolveDmaName(config, code))
      .filter(Boolean);
    const value = names.length === 0
      ? ''
      : names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1} more`;
    return { icon: MapPin, label: 'Metros', value };
  }

  if (targetingMode === 'default') {
    const channels = config.markets[0]?.channels ?? [];
    return {
      icon: Radio,
      label: 'Channel',
      value: channels.join(', ') || displayedName(config as PublisherConfig),
    };
  }

  // Custom mode — switch on geoMode so the row tracks the active tab.
  const geoMode = input.geoMode ?? 'radius';
  if (geoMode === 'states') {
    const names = (input.selectedStates ?? []).map(stateNameForCode);
    return { icon: MapPin, label: 'Targeting', value: summarize('states', names) || 'Custom geotargeting' };
  }
  if (geoMode === 'dmas') {
    const names = (input.selectedDMAs ?? []).map(dmaNameForCode);
    return { icon: MapPin, label: 'Targeting', value: summarize('Metros', names) || 'Custom geotargeting' };
  }
  // radius
  const miles = input.geoTargetRadiusMiles ?? 0;
  const center = input.geoTargetCenter?.label;
  const value = center
    ? `${miles}mi around ${center}`
    : miles > 0
      ? `${miles}mi radius`
      : 'Custom geotargeting';
  return { icon: MapPin, label: 'Targeting', value };
};

export const targetingRowFromFormValues = (
  values: Pick<SelfServiceFormData,
    'targetMarkets' | 'targetingMode' | 'geoMode' | 'selectedStates' | 'selectedDMAs'
    | 'geoTargetCenter' | 'geoTargetRadiusMiles' | 'geoTargetZips'>,
  config: PublisherConfig,
): TargetingRow => getTargetingRow(values, config);
